/**
 * @module BriefingJobStatus
 * @purpose Inline status display for an active briefing generation job (FR5).
 *   Shows queued/running spinner, succeeded indicator, or failed with errorCategory.
 * @depends types/reasoning, react-i18next, lucide-react
 * @dependedOnBy BriefingTile
 *
 * @rules
 * - Performance: pure display — all data passed via props
 * - State: none
 * - Errors: n/a — purely presentational
 */

import { useTranslation } from 'react-i18next'
import { Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import type { BriefingJob } from '../../../../../types/reasoning'

interface BriefingJobStatusProps {
  job: BriefingJob
}

const STATUS_ROW: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  marginTop: '6px',
  fontFamily: 'var(--font-mono)',
  fontSize: '0.72rem',
}

const ERROR_CAT_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.65rem',
  color: 'var(--text-dim)',
  marginLeft: '4px',
}

/** Failure detail line — indented under the status row, wraps rather than truncating. */
const ERROR_DETAIL_STYLE: React.CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: '0.62rem',
  color: 'var(--text-dim)',
  marginLeft: '18px',
  marginTop: '2px',
  overflowWrap: 'anywhere',
}

const ERROR_CATEGORY_LABELS: Record<string, string> = {
  provider_unreachable: 'reasoning.briefingBoard.generate.errorCategory.provider_unreachable',
  rate_limited: 'reasoning.briefingBoard.generate.errorCategory.rate_limited',
  invalid_output: 'reasoning.briefingBoard.generate.errorCategory.invalid_output',
  internal: 'reasoning.briefingBoard.generate.errorCategory.internal',
}

/**
 * Inline display of the job lifecycle for a briefing generation job.
 *
 * @behaviour queued and running show a spinner with the job status label.
 *   succeeded shows a check. failed shows an alert icon + errorCategory.
 *   Polling states include a note that local generation takes minutes.
 * @errors n/a — purely presentational
 */
export function BriefingJobStatus({ job }: BriefingJobStatusProps): React.ReactElement | null {
  const { t } = useTranslation('analysis')

  if (job.status === 'queued' || job.status === 'running') {
    return (
      <div style={STATUS_ROW} role="status" aria-busy="true">
        <Loader2
          className="h-3.5 w-3.5 animate-spin"
          style={{ color: 'var(--accent)', flexShrink: 0 }}
        />
        <span style={{ color: 'var(--text-dim)' }}>
          {t(`reasoning.briefingBoard.jobStatus.${job.status}` as 'reasoning.briefingBoard.jobStatus.queued')}
        </span>
      </div>
    )
  }

  if (job.status === 'succeeded') {
    return (
      <div style={STATUS_ROW} role="status">
        <CheckCircle2
          className="h-3.5 w-3.5"
          style={{ color: 'green', flexShrink: 0 }}
        />
        <span style={{ color: 'var(--text-dim)' }}>
          {t('reasoning.briefingBoard.jobStatus.succeeded')}
        </span>
      </div>
    )
  }

  const catKey = job.errorCategory ? ERROR_CATEGORY_LABELS[job.errorCategory] : undefined
  const catLabel = catKey
    ? t(catKey as 'reasoning.briefingBoard.generate.errorCategory.internal')
    : job.errorCategory ?? ''
  return (
    <div role="alert">
      <div style={STATUS_ROW}>
        <AlertCircle
          className="h-3.5 w-3.5"
          style={{ color: 'var(--color-red-600, #dc2626)', flexShrink: 0 }}
        />
        <span style={{ color: 'var(--color-red-600, #dc2626)' }}>
          {t('reasoning.briefingBoard.generate.failed')}
        </span>
        {catLabel && <span style={ERROR_CAT_STYLE}>({catLabel})</span>}
      </div>
      {/*
        The category alone ("internal", "provider_unreachable") does not tell an operator what to
        do. `errorDetail` is the actual reason and was recorded on the job row all along — it was
        simply never mapped into the API response. It is redaction-safe by construction; see
        `safe_error_detail` in services/ai_summary/jobs.rs.
      */}
      {job.errorDetail && <div style={ERROR_DETAIL_STYLE}>{job.errorDetail}</div>}
    </div>
  )
}
